import { createFileRoute, Link, useNavigate, retainSearchParams } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  listCycleCounts, createCycleCount, deleteCycleCount,
} from "@/lib/cycle-counts.functions";
import { listCustomersOptions } from "@/lib/admin-config.functions";
import { runServerFn } from "@/lib/server-fn";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/operator-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { StatusPill, statusTone } from "@/components/ui/status-pill";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Loader2, ClipboardCheck, ChevronRight, Trash2, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { HelpButton } from "@/components/kb/help-button";
import { ContextualTip } from "@/components/kb/contextual-tip";
import { DateRangeFilter } from "@/components/filters/date-range-filter";
import { DateFieldSelect } from "@/components/filters/date-field-select";
import { SortDirectionToggle } from "@/components/filters/sort-direction-toggle";
import { dateRangeSearchSchema, dateRangeToBounds, retainDateRangeKeys } from "@/lib/date-range-search";

const cycleCountsSearchSchema = dateRangeSearchSchema.extend({
  dateField: fallback(z.enum(["created_at", "posted_at"]).optional(), undefined),
  status: fallback(z.enum(["open", "posted", "cancelled"]).optional(), undefined),
  customerId: fallback(z.string().uuid().optional(), undefined),
});

export const Route = createFileRoute("/_authenticated/app/cycle-counts/")({
  validateSearch: zodValidator(cycleCountsSearchSchema),
  search: { middlewares: [retainSearchParams([...retainDateRangeKeys])] },
  component: CycleCountsPage,
});

function CycleCountsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const urlSearch = Route.useSearch();
  const navigateSearch = useNavigate({ from: Route.fullPath });
  const dateField = (urlSearch.dateField ?? "created_at") as "created_at" | "posted_at";
  const sort = urlSearch.sort ?? "desc";
  const status = urlSearch.status;
  const { fromIso, toIso } = useMemo(
    () => dateRangeToBounds({ start: urlSearch.start, end: urlSearch.end }),
    [urlSearch.start, urlSearch.end],
  );
  const { tenantId, facilityId, customerId: scopeCustomerId } = useScope();
  // Scope-bar customer takes precedence; URL filter is a local override.
  const customerId = urlSearch.customerId ?? scopeCustomerId ?? undefined;
  const listFn = useServerFn(listCycleCounts);
  const createFn = useServerFn(createCycleCount);
  const deleteFn = useServerFn(deleteCycleCount);
  const customersFn = useServerFn(listCustomersOptions);

  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const { data: rows, isLoading } = useQuery({
    queryKey: ["cycle-counts", tenantId, facilityId, fromIso, toIso, dateField, sort, status, customerId],
    enabled: !!tenantId && !!facilityId,
    queryFn: () => listFn({ data: { tenantId: tenantId!, facilityId: facilityId!, fromIso, toIso, dateField, sort, status, customerId } }),
  });
  const { data: customers = [] } = useQuery({
    queryKey: ["cc-page-customers", tenantId],
    enabled: !!tenantId,
    queryFn: () => customersFn(),
  });
  const customerName = (id?: string | null) =>
    id ? (customers.find((c: any) => c.id === id)?.name ?? "—") : "—";

  const createCount = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tenantId || !facilityId || !name.trim()) return;
    setSubmitting(true);
    try {
      const res = await runServerFn(
        () => createFn({ data: { tenantId, facilityId, name: name.trim(), notes: notes.trim() || null } }),
        "Create cycle count",
      );
      toast.success("Cycle count created");
      setOpen(false); setName(""); setNotes("");
      qc.invalidateQueries({ queryKey: ["cycle-counts", tenantId, facilityId] });
      if (res?.id) navigate({ to: "/app/cycle-counts/$id", params: { id: res.id } });
    } catch { /* toast handled */ } finally { setSubmitting(false); }
  };

  const remove = async (e: React.MouseEvent, rowId: string, status: string) => {
    e.stopPropagation();
    if (status === "posted") return;
    if (!confirm("Delete this cycle count? Lines will be removed.")) return;
    try {
      await runServerFn(() => deleteFn({ data: { id: rowId } }), "Delete cycle count");
      toast.success("Cycle count deleted");
      qc.invalidateQueries({ queryKey: ["cycle-counts", tenantId, facilityId] });
    } catch { /* toast handled */ }
  };

  return (
    <>
      <PageHeader
        title="Cycle Counts"
        description="Recount locations and post variances to the inventory ledger."
        actions={
          <>
          <HelpButton moduleKey="cycle_counts" />
          <Link to="/app/admin/cycle-count-schedules">
            <Button variant="outline">
              <CalendarClock className="h-4 w-4 mr-2" /> Schedules
            </Button>
          </Link>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button disabled={!tenantId || !facilityId}><Plus className="h-4 w-4 mr-2" />New count</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Create cycle count</DialogTitle></DialogHeader>
              <form onSubmit={createCount} className="space-y-4">
                <div className="space-y-2"><Label>Name</Label><Input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Zone A weekly" /></div>
                <div className="space-y-2"><Label>Notes</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" /></div>
                <DialogFooter><Button type="submit" disabled={submitting}>{submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Create</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </>
        }
      />
      <div className="p-4 sm:p-6">
        <div className="mb-4"><ContextualTip tipKey="cycle_count_empty" /></div>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Select
            value={status ?? "_all"}
            onValueChange={(v) => navigateSearch({ search: (p: typeof urlSearch) => ({ ...p, status: v === "_all" ? undefined : (v as typeof urlSearch.status) }) })}
          >
            <SelectTrigger className="h-8 w-[140px]"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All statuses</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="posted">Posted</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={customerId ?? "_all"}
            onValueChange={(v) => navigateSearch({ search: (p: typeof urlSearch) => ({ ...p, customerId: v === "_all" ? undefined : v }) })}
          >
            <SelectTrigger className="h-8 w-[180px]"><SelectValue placeholder="Customer" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="_all">All customers</SelectItem>
              {customers.map((c: any) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DateFieldSelect
            value={dateField}
            onChange={(v) => navigateSearch({ search: (p: typeof urlSearch) => ({ ...p, dateField: v === "created_at" ? undefined : (v as typeof urlSearch.dateField) }) })}
            options={[
              { value: "created_at", label: "Created" },
              { value: "posted_at", label: "Posted" },
            ]}
          />
          <DateRangeFilter
            value={{ start: urlSearch.start, end: urlSearch.end }}
            onChange={(r) => navigateSearch({ search: (p: typeof urlSearch) => ({ ...p, start: r.start, end: r.end }) })}
          />
          <SortDirectionToggle
            value={sort}
            onChange={(s) => navigateSearch({ search: (p: typeof urlSearch) => ({ ...p, sort: s === "desc" ? undefined : s }) })}
          />
        </div>
        <div className="border rounded-lg bg-card overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Posted</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">Loading…</TableCell></TableRow>
              ) : rows && rows.length > 0 ? rows.map((r) => (
                <TableRow key={r.id} className="cursor-pointer" onClick={() => navigate({ to: "/app/cycle-counts/$id", params: { id: r.id } })}>
                  <TableCell className="font-medium flex items-center gap-2"><ClipboardCheck className="h-4 w-4 text-muted-foreground" />{r.name}</TableCell>
                  <TableCell><StatusPill tone={statusTone(r.status)} dot>{r.status}</StatusPill></TableCell>
                  <TableCell className="text-muted-foreground text-sm">{customerName((r as any).customer_id)}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{new Date(r.created_at).toLocaleString()}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{r.posted_at ? new Date(r.posted_at).toLocaleString() : "—"}</TableCell>
                  <TableCell className="text-right">
                    {r.status !== "posted" && (
                      <Button variant="ghost" size="icon" onClick={(e) => remove(e, r.id, r.status)} aria-label="Delete">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                    <ChevronRight className="h-4 w-4 text-muted-foreground inline ml-1" />
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">No cycle counts yet. Create one to get started.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <p className="text-xs text-muted-foreground mt-3">
          Tip: open a count to add lines from your locations, then enter the physical counts and post.
          <Link to="/app/inventory" className="ml-1 underline">View current inventory</Link>
        </p>
      </div>
    </>
  );
}